import type { ParsedUrlQuery } from 'querystring';

import {
  SHOP_FILTER_SEPARATOR,
  SHOP_PATH_PREFIX,
  SHOP_TAXONOMY_SKIP_SEGMENT,
} from 'src/adapters/pageURLToStore/shop/constants';

/** One path segment slot: a single L1 slug, `all` (skip), or multiple slugs (L2–4, OR in Vertex). */
export type ShopTaxonomyLevels = string[][];

export type ParseShopPathResult =
  | { ok: true; taxonomyLevels: ShopTaxonomyLevels }
  | { error: string; ok: false };

/**
 * Percent-encoding for `/shop/{l1}/…` segments. Uses `encodeURIComponent` so spaces,
 * `&`, and other reserved characters round-trip safely.
 */
const encodeSegment = (segment: string): string => encodeURIComponent(segment);

const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const dedupeSlugsPreserveOrder = (slugs: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of slugs) {
    if (!seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  return out;
};

export const isTaxonomySkipSlot = (slot: string[]): boolean =>
  slot.length === 1 && slot[0] === SHOP_TAXONOMY_SKIP_SEGMENT;

const serializeShopTaxonomySlot = (slot: string[]): string => {
  if (isTaxonomySkipSlot(slot)) {
    return encodeSegment(SHOP_TAXONOMY_SKIP_SEGMENT);
  }
  return dedupeSlugsPreserveOrder(slot).map(encodeSegment).join(',');
};

const parseRawTaxonomySegmentToSlot = (
  rawSegment: string,
  levelIndex: number,
): { ok: true; slot: string[] } | { error: string; ok: false } => {
  if (levelIndex === 0) {
    if (rawSegment.includes(',')) {
      return { ok: false, error: 'Invalid shop path' };
    }
    const v = decodeSegment(rawSegment);
    if (v === SHOP_TAXONOMY_SKIP_SEGMENT) {
      return { ok: false, error: '"all" is not valid as the first taxonomy segment' };
    }
    if (v.includes(',')) {
      return { ok: false, error: 'Invalid shop path' };
    }
    return { ok: true, slot: [v] };
  }

  const rawParts = rawSegment.split(',');
  if (rawParts.length === 0 || rawParts.some((p) => p.trim() === '')) {
    return { ok: false, error: 'Invalid shop path' };
  }

  const parts = rawParts.map((p) => decodeSegment(p.trim()));

  // If any value is `all`, skip this level.
  if (parts.some((p) => p === SHOP_TAXONOMY_SKIP_SEGMENT)) {
    return { ok: true, slot: [SHOP_TAXONOMY_SKIP_SEGMENT] };
  }
  if (parts.some((p) => p.includes(','))) {
    return { ok: false, error: 'Invalid shop path' };
  }

  // Duplicate slugs in one segment are tolerated; first occurrence order is kept.
  return { ok: true, slot: dedupeSlugsPreserveOrder(parts) };
};

const normalizePathname = (pathname: string): string => {
  const trimmed = pathname.trim();
  if (trimmed.length > 1 && trimmed.endsWith('/')) {
    return trimmed.slice(0, -1);
  }
  return trimmed;
};

const segmentsOf = (pathname: string): string[] =>
  normalizePathname(pathname)
    .split('/')
    .filter((s) => s.length > 0);

/**
 * Parses `/shop/...` pathname (no query string).
 * L1: single slug (no comma). L2–4: comma-separated slugs (OR) or whole-segment `all` (skip; never in Vertex `text`).
 * A path segment that is exactly `-` ends taxonomy; any segments after it are ignored for now (legacy path filters).
 * Trailing `all` slots are stripped.
 */
export const parseShopPath = (pathname: string): ParseShopPathResult => {
  const segments = segmentsOf(pathname);

  if (segments.length === 0 || segments[0] !== SHOP_PATH_PREFIX) {
    return { ok: false, error: 'Path must start with /shop' };
  }

  const rest = segments.slice(1);

  if (rest.length === 0) {
    return { ok: false, error: 'Shop PLP requires at least one taxonomy segment' };
  }

  const sepIndex = rest.indexOf(SHOP_FILTER_SEPARATOR);
  const taxonomyPart = sepIndex === -1 ? rest : rest.slice(0, sepIndex);

  if (taxonomyPart.length === 0) {
    return { ok: false, error: 'Missing taxonomy before reserved hyphen segment' };
  }

  if (taxonomyPart.length > 4) {
    return { ok: false, error: 'At most four taxonomy levels are supported' };
  }

  const slots: ShopTaxonomyLevels = [];
  for (let i = 0; i < taxonomyPart.length; i++) {
    const parsed = parseRawTaxonomySegmentToSlot(taxonomyPart[i], i);
    if (!parsed.ok) {
      return { ok: false, error: parsed.error };
    }
    slots.push(parsed.slot);
  }

  let taxonomyLevels = slots;
  while (
    taxonomyLevels.length > 0 &&
    isTaxonomySkipSlot(taxonomyLevels[taxonomyLevels.length - 1]!)
  ) {
    taxonomyLevels = taxonomyLevels.slice(0, -1);
  }

  if (taxonomyLevels.length === 0) {
    return { ok: false, error: 'Taxonomy resolves to empty after removing trailing "all"' };
  }

  return { ok: true, taxonomyLevels };
};

/** Builds the `/shop/<L1>/<L2>/...` pathname (no query string) from parsed levels. */
export const buildShopPath = (taxonomyLevels: ShopTaxonomyLevels): string =>
  `/${SHOP_PATH_PREFIX}/${taxonomyLevels.map(serializeShopTaxonomySlot).join('/')}`;

/**
 * Vertex / listings page-category string from shop taxonomy segments (breadcrumb-style).
 * Skipped levels (`all`) are omitted from the label.
 */
export const levelsToPageCategories = (taxonomyLevels: ShopTaxonomyLevels): string[] => [
  taxonomyLevels
    .filter((slot) => !isTaxonomySkipSlot(slot))
    .map((slot) => slot.map((s) => s.replace(/-/g, ' ')).join(', '))
    .join(' > '),
];

/**
 * Used by the shop SSR page. Parses `/shop/...` and strips Next.js's catch-all `slug` from the
 * router query so what remains can flow through `adaptPageURLToStore` like any other PLP query.
 */
export const mergeShopPathIntoQuery = (
  pathname: string,
  routerQuery: ParsedUrlQuery,
): { mergedQuery: ParsedUrlQuery; shopTaxonomyLevels: ShopTaxonomyLevels } | { error: string } => {
  const parsed = parseShopPath(pathname);
  if (!parsed.ok) {
    return { error: parsed.error };
  }
  const { slug: _slug, ...mergedQuery } = routerQuery;
  return { mergedQuery, shopTaxonomyLevels: parsed.taxonomyLevels };
};
